import type { ComputedRef } from 'vue';

// Module/Page-based Permission System
export enum Permissions {
  // Dashboard & Home
  DASHBOARD_READ = "dashboard:read",
  
  // Calculator Module
  CALCULATOR_READ = "calculator:read",
  CALCULATOR_WRITE = "calculator:write",
  CALCULATOR_DELETE = "calculator:delete",
  CALCULATOR_BULK_UPLOAD = "calculator:bulk_upload",
  CALCULATOR_EXPORT = "calculator:export",
  
  // Analysis Module
  ANALYSIS_READ = "analysis:read",
  ANALYSIS_EXPORT = "analysis:export",
  ANALYSIS_ADVANCED_FILTERS = "analysis:advanced_filters",
  
  // Reports Module
  REPORTS_READ = "reports:read",
  REPORTS_GENERATE = "reports:generate",
  REPORTS_EXPORT = "reports:export",
  REPORTS_SCHEDULE = "reports:schedule",
  
  // Settings Module
  SETTINGS_READ = "settings:read",
  SETTINGS_WRITE = "settings:write",
  SETTINGS_FACILITIES_MANAGE = "settings:facilities_manage",
  SETTINGS_EMISSIONS_MANAGE = "settings:emissions_manage",
  SETTINGS_USERS_MANAGE = "settings:users_manage",
  
  // Targets Module
  TARGETS_READ = "targets:read",
  TARGETS_WRITE = "targets:write",
  TARGETS_DELETE = "targets:delete",
  
  // Decarbonization Module
  DECARBONIZATION_READ = "decarbonization:read",
  DECARBONIZATION_WRITE = "decarbonization:write",
  DECARBONIZATION_PROJECTS_MANAGE = "decarbonization:projects_manage",
  
  
  // Data Hub Module
  DATA_HUB_READ = "data_hub:read",
  DATA_HUB_QUERY_CREATE = "data_hub:query_create",
  DATA_HUB_QUERY_SAVE = "data_hub:query_save",
  DATA_HUB_EXPORT = "data_hub:export",
  
  // GHG Reports
  GHG_REPORTS_READ = "ghg_reports:read",
  GHG_REPORTS_GENERATE = "ghg_reports:generate",
  GHG_REPORTS_SUBMIT = "ghg_reports:submit",
  
  // Survey Module
  SURVEY_READ = "survey:read",
  SURVEY_WRITE = "survey:write",
  
  // Company Information
  COMPANY_INFO_READ = "company_info:read",
  COMPANY_INFO_WRITE = "company_info:write",
  
  // User Management
  USER_PROFILE_READ = "user_profile:read",
  USER_PROFILE_WRITE = "user_profile:write",
  
  // Admin Operations
  ADMIN_COMPANY_SWITCH = "admin:company_switch",
  ADMIN_SYSTEM_SETTINGS = "admin:system_settings",
  ADMIN_USER_MANAGEMENT = "admin:user_management",
}

// Permission categorization for easier management
export const PermissionCategories = {
  DASHBOARD: [
    Permissions.DASHBOARD_READ,
  ],
  CALCULATOR: [
    Permissions.CALCULATOR_READ,
    Permissions.CALCULATOR_WRITE,
    Permissions.CALCULATOR_DELETE,
    Permissions.CALCULATOR_BULK_UPLOAD,
    Permissions.CALCULATOR_EXPORT,
  ],
  ANALYSIS: [
    Permissions.ANALYSIS_READ,
    Permissions.ANALYSIS_EXPORT,
    Permissions.ANALYSIS_ADVANCED_FILTERS,
  ],
  REPORTS: [
    Permissions.REPORTS_READ,
    Permissions.REPORTS_GENERATE,
    Permissions.REPORTS_EXPORT,
    Permissions.REPORTS_SCHEDULE,
  ],
  SETTINGS: [
    Permissions.SETTINGS_READ,
    Permissions.SETTINGS_WRITE,
    Permissions.SETTINGS_FACILITIES_MANAGE,
    Permissions.SETTINGS_EMISSIONS_MANAGE,
    Permissions.SETTINGS_USERS_MANAGE,
  ],
  TARGETS: [
    Permissions.TARGETS_READ,
    Permissions.TARGETS_WRITE,
    Permissions.TARGETS_DELETE,
  ],
  DECARBONIZATION: [
    Permissions.DECARBONIZATION_READ,
    Permissions.DECARBONIZATION_WRITE,
    Permissions.DECARBONIZATION_PROJECTS_MANAGE,
  ],
  DATA_HUB: [
    Permissions.DATA_HUB_READ,
    Permissions.DATA_HUB_QUERY_CREATE,
    Permissions.DATA_HUB_QUERY_SAVE,
    Permissions.DATA_HUB_EXPORT,
  ],
  GHG_REPORTS: [
    Permissions.GHG_REPORTS_READ,
    Permissions.GHG_REPORTS_GENERATE,
    Permissions.GHG_REPORTS_SUBMIT,
  ],
  SURVEY: [
    Permissions.SURVEY_READ,
    Permissions.SURVEY_WRITE,
  ],
  COMPANY_INFO: [
    Permissions.COMPANY_INFO_READ,
    Permissions.COMPANY_INFO_WRITE,
  ],
  USER_PROFILE: [
    Permissions.USER_PROFILE_READ,
    Permissions.USER_PROFILE_WRITE,
  ],
  ADMIN: [
    Permissions.ADMIN_COMPANY_SWITCH,
    Permissions.ADMIN_SYSTEM_SETTINGS,
    Permissions.ADMIN_USER_MANAGEMENT,
  ],
} as const;

// Helper types for type safety
export type PermissionKey = keyof typeof Permissions;
export type PermissionValue = typeof Permissions[PermissionKey];
export type PermissionCategory = keyof typeof PermissionCategories;

// Module permission type mappings for better TypeScript support
export interface ModulePermissionMap {
  DASHBOARD: {
    canRead: boolean;
  };
  CALCULATOR: {
    canRead: boolean;
    canWrite: boolean;
    canDelete: boolean;
    canBulkUpload: boolean;
    canExport: boolean;
  };
  ANALYSIS: {
    canRead: boolean;
    canExport: boolean;
    canAdvancedFilters: boolean;
  };
  REPORTS: {
    canRead: boolean;
    canGenerate: boolean;
    canExport: boolean;
    canSchedule: boolean;
  };
  SETTINGS: {
    canRead: boolean;
    canWrite: boolean;
    canFacilitiesManage: boolean;
    canEmissionsManage: boolean;
    canUsersManage: boolean;
  };
  TARGETS: {
    canRead: boolean;
    canWrite: boolean;
    canDelete: boolean;
  };
  DECARBONIZATION: {
    canRead: boolean;
    canWrite: boolean;
    canProjectsManage: boolean;
  };
  DATA_HUB: {
    canRead: boolean;
    canQueryCreate: boolean;
    canQuerySave: boolean;
    canExport: boolean;
  };
  GHG_REPORTS: {
    canRead: boolean;
    canGenerate: boolean;
    canSubmit: boolean;
  };
  SURVEY: {
    canRead: boolean;
    canWrite: boolean;
  };
  COMPANY_INFO: {
    canRead: boolean;
    canWrite: boolean;
  };
  USER_PROFILE: {
    canRead: boolean;
    canWrite: boolean;
  };
  ADMIN: {
    canCompanySwitch: boolean;
    canSystemSettings: boolean;
    canUserManagement: boolean;
  };
}

// Computed ref versions for reactive usage
export type ModulePermissionComputed<T extends PermissionCategory> = {
  [K in keyof ModulePermissionMap[T]]: ComputedRef<ModulePermissionMap[T][K]>;
};

// Helper function to convert permission names to camelCase method names
export const permissionToMethodName = (permission: string): string => {
  // "calculator:read" -> "canRead"
  // "calculator:bulk_upload" -> "canBulkUpload"
  const action = permission.split(':')[1];
  if (!action) return 'can';
  
  return 'can' + action
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');
};